import { useMemo, useState } from 'react'
import {
	DndContext,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
	type DragEndEvent,
} from '@dnd-kit/core'
import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ArrowDown, ArrowUp, GripVertical, Search, X } from 'lucide-react'

import { Checkbox } from 'components/ui/checkbox'
import { Input } from 'components/ui/input'
import { Sheet } from 'components/ui/sheet'
import { cn } from 'lib/utils'

import { COLUMN_CAP, GROUP_ORDER } from '../constants'
import type { ExplorerColumn } from '../types/explorer.types'
import PopupCloseButton from 'components/PopupCloseButton'

interface ColumnPickerSheetProps {
	open: boolean
	onClose: () => void
	/** The server-driven catalog. The picker never holds its own column list. */
	catalog: ExplorerColumn[]
	/** Ordered selected keys. */
	selected: string[]
	onChange: (columns: string[]) => void
}

/**
 * The column picker (MYD-361).
 *
 * Two panes: the ordered selection, which is what the grid renders left to
 * right, and the searchable catalog underneath it. Reordering is the reason
 * this is a sheet rather than a dropdown — column order is part of the report,
 * and a menu that closes on every click makes arranging twenty columns
 * miserable.
 *
 * `components/ui/sheet.tsx` is a hand-rolled portal, NOT Radix: its prop is
 * `onClose`, not `onOpenChange`, and it has no header subcomponents, so the
 * chrome is built here. Its panel is 280px by default, which is too narrow for
 * two panes, so the width is overridden through `className`.
 */
export function ColumnPickerSheet({
	open,
	onClose,
	catalog,
	selected,
	onChange,
}: ColumnPickerSheetProps) {
	const [search, setSearch] = useState('')

	const byKey = useMemo(() => new Map(catalog.map(column => [column.key, column])), [catalog])

	/**
	 * Selected columns as definitions, in the user's order.
	 *
	 * A key that no longer resolves is dropped rather than rendered blank: a
	 * saved report can outlive a column, and the grid already surfaces the
	 * unresolved key in its header.
	 */
	const selectedColumns = useMemo(
		() => selected.map(key => byKey.get(key)).filter((c): c is ExplorerColumn => Boolean(c)),
		[selected, byKey],
	)

	const atCap = selected.length >= COLUMN_CAP

	/** The catalog minus what is already selected, filtered and grouped. */
	const groups = useMemo(() => {
		const query = search.trim().toLowerCase()
		const chosen = new Set(selected)

		const available = catalog.filter(column => {
			if (chosen.has(column.key)) return false
			if (!query) return true
			// Match the group too, so "address" surfaces every address column
			// rather than only the one literally called Address.
			return (
				column.label.toLowerCase().includes(query) || column.group.toLowerCase().includes(query)
			)
		})

		const byGroup = new Map<string, ExplorerColumn[]>()
		for (const column of available) {
			const list = byGroup.get(column.group) ?? []
			list.push(column)
			byGroup.set(column.group, list)
		}

		// Known groups first in their declared order, then anything the client
		// has not been taught about, so a new backend group still appears.
		return [...byGroup.entries()].sort(([a], [b]) => {
			const ai = GROUP_ORDER.indexOf(a)
			const bi = GROUP_ORDER.indexOf(b)
			if (ai === -1 && bi === -1) return a.localeCompare(b)
			if (ai === -1) return 1
			if (bi === -1) return -1
			return ai - bi
		})
	}, [catalog, selected, search])

	const sensors = useSensors(
		// A small distance threshold, so a click to remove a column is not
		// swallowed as the start of a drag.
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	)

	const add = (key: string) => {
		if (atCap) return
		onChange([...selected, key])
	}

	const remove = (key: string) => onChange(selected.filter(existing => existing !== key))

	const move = (key: string, delta: -1 | 1) => {
		const from = selected.indexOf(key)
		const to = from + delta
		if (from < 0 || to < 0 || to >= selected.length) return
		onChange(arrayMove(selected, from, to))
	}

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event
		if (!over || active.id === over.id) return
		const from = selected.indexOf(String(active.id))
		const to = selected.indexOf(String(over.id))
		if (from < 0 || to < 0) return
		onChange(arrayMove(selected, from, to))
	}

	return (
		<Sheet open={open} onClose={onClose} side="right" className="w-[400px] sm:w-[460px]">
			<div className="flex items-center justify-between px-4 h-14 border-b border-gray-200 shrink-0">
				<div>
					<h2 className="text-base font-semibold text-gray-900">Columns</h2>
					<p className={cn('text-xs', atCap ? 'text-destructive' : 'text-gray-500')}>
						{selected.length} of {COLUMN_CAP} selected
					</p>
				</div>
				<PopupCloseButton onClose={onClose} />
			</div>

			<div className="flex-1 overflow-y-auto">
				<section className="px-4 py-3 border-b border-gray-200">
					<h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
						In this report
					</h3>

					{selectedColumns.length === 0 ? (
						<p className="text-sm text-gray-500 py-2">
							No columns yet. Add some from the list below.
						</p>
					) : (
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
						>
							<SortableContext
								items={selected}
								strategy={verticalListSortingStrategy}
							>
								<ul className="space-y-1">
									{selectedColumns.map((column, index) => (
										<SortableColumnRow
											key={column.key}
											column={column}
											isFirst={index === 0}
											isLast={index === selectedColumns.length - 1}
											onRemove={() => remove(column.key)}
											onMove={delta => move(column.key, delta)}
										/>
									))}
								</ul>
							</SortableContext>
						</DndContext>
					)}
				</section>

				<section className="px-4 py-3">
					<div className="relative mb-3">
						<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
						<Input
							value={search}
							onChange={event => setSearch(event.target.value)}
							placeholder="Search columns..."
							className="pl-8"
							aria-label="Search columns"
						/>
					</div>

					{atCap && (
						<p className="text-xs text-destructive mb-3">
							You have reached the {COLUMN_CAP} column limit. Remove one to add another.
						</p>
					)}

					{groups.length === 0 ? (
						<p className="text-sm text-gray-500 py-2">
							{search ? 'No columns match your search.' : 'Every column is already in the report.'}
						</p>
					) : (
						groups.map(([group, columns]) => (
							<div key={group} className="mb-4 last:mb-0">
								<h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
									{group}
								</h3>
								<ul className="space-y-0.5">
									{columns.map(column => (
										<li key={column.key}>
											<button
												type="button"
												disabled={atCap}
												onClick={() => add(column.key)}
												className={cn(
													'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm transition-colors',
													atCap
														? 'text-gray-400 cursor-not-allowed'
														: 'text-gray-700 hover:bg-gray-50',
												)}
											>
												{/* Decorative: the row itself is the control, so the
												    checkbox must not be separately focusable. */}
												<Checkbox checked={false} disabled={atCap} />
												<span className="truncate">{column.label}</span>
											</button>
										</li>
									))}
								</ul>
							</div>
						))
					)}
				</section>
			</div>
		</Sheet>
	)
}

function SortableColumnRow({
	column,
	isFirst,
	isLast,
	onRemove,
	onMove,
}: {
	column: ExplorerColumn
	isFirst: boolean
	isLast: boolean
	onRemove: () => void
	onMove: (delta: -1 | 1) => void
}) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: column.key,
	})

	return (
		<li
			ref={setNodeRef}
			style={{ transform: CSS.Transform.toString(transform), transition }}
			className={cn(
				'flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-gray-200 bg-white text-sm',
				isDragging && 'opacity-50 shadow-md',
			)}
		>
			<button
				type="button"
				className="cursor-grab text-gray-400 hover:text-gray-600 shrink-0"
				aria-label={`Reorder ${column.label}`}
				{...attributes}
				{...listeners}
			>
				<GripVertical className="w-4 h-4" />
			</button>

			<span className="flex-1 truncate text-gray-800">{column.label}</span>

			{/* Explicit move buttons alongside the drag handle. dnd-kit's keyboard
			    sensor already supports lift-and-move, but it is invisible: a user
			    has to know to press space on the handle first. These are the
			    discoverable path, and the only one on a touch screen where a
			    precise drag is awkward. */}
			<button
				type="button"
				disabled={isFirst}
				onClick={() => onMove(-1)}
				aria-label={`Move ${column.label} earlier`}
				className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-400 shrink-0"
			>
				<ArrowUp className="w-3.5 h-3.5" />
			</button>
			<button
				type="button"
				disabled={isLast}
				onClick={() => onMove(1)}
				aria-label={`Move ${column.label} later`}
				className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-400 shrink-0"
			>
				<ArrowDown className="w-3.5 h-3.5" />
			</button>
			<button
				type="button"
				onClick={onRemove}
				aria-label={`Remove ${column.label}`}
				className="p-0.5 text-gray-400 hover:text-destructive shrink-0"
			>
				<X className="w-3.5 h-3.5" />
			</button>
		</li>
	)
}

export default ColumnPickerSheet
